import type { SurveyQuestion, SurveyResponse, SurveySettings } from "./staffSatisfactionTypes";

export const SURVEY_QUESTIONS: SurveyQuestion[] = [
	{
		id: "q1",
		text: "How would you rate your last shift overall?",
		aiSummary:
			'Staff sentiment is positive among nurses due to clinical stability, while CNAs reported fatigue from logistical hurdles. Despite an "exhaustion gap" for aides, strong teamwork ensured successful task completion.',
		isActive: true,
		score: 3.8,
	},
	{
		id: "q2",
		text: "Did you feel adequately staffed during your shift?",
		aiSummary:
			'Staff sentiment is split by role: RNs and LPNs report satisfaction, while CNAs describe "unsafe" ratios. Though nursing "grids" are met, frontline aides are overextended and struggling to complete basic ADL care.',
		isActive: true,
		score: 2.6,
	},
	{
		id: "q3",
		text: "Did you have the supplies, support, and resources you needed?",
		aiSummary:
			"Staff reported a major disconnect between supply availability and equipment functionality, citing frequent failures of lifts and vitals machines. Shortages of essentials like wipes and linens remain a recurring issue. Inconsistent management support often forces frontline staff to prioritize solving logistical hurdles over direct resident care.",
		isActive: true,
		score: 2.5,
	},
	{
		id: "q4",
		text: "Did anything happen during your shift that made your work harder than it should be?",
		aiSummary:
			"Staff reported a highly productive shift with minimal friction and no typical stressors like call-outs or equipment failure. This stability allowed for focused resident care and meaningful interactions. Clear communication and visible leadership support ensured the workload remained balanced and manageable for the entire team.",
		isActive: true,
		score: 4.6,
	},
	{
		id: "q5",
		text: "Were there any patient care situations you felt understaffed for?",
		aiSummary:
			"Staff across all roles reported high confidence in patient safety and care delivery, with zero reports of delayed care. Teamwork was seamless, specifically regarding two-person tasks like Hoyer transfers. Current staffing levels provided a robust and effective safety net for all resident needs.",
		isActive: true,
		score: 4.8,
	},
	{
		id: "q6",
		text: "How stressful was your shift?",
		aiSummary: "Stress levels were reported as moderate to high. High acuity patients and unexpected call-outs contributed significantly to the stress burden.",
		isActive: false,
		score: 2.7,
	},
	{
		id: "q7",
		text: "Was there any delay or issue during shift handoff?",
		aiSummary: "Handoffs were mostly smooth, but some delays occurred due to pending lab results and waiting for physicians to complete rounds.",
		isActive: false,
		score: 3.6,
	},
	{
		id: "q8",
		text: "Did you notice any safety concerns during your shift?",
		aiSummary: "A few minor concerns were raised regarding cluttered hallways near the supply room and delayed response times from security.",
		isActive: false,
		score: 3.1,
	},
	{
		id: "q9",
		text: "Is there one thing we could improve before your next shift?",
		aiSummary: "The most requested improvements are better communication regarding staffing changes and ensuring equipment is restocked between shifts.",
		isActive: false,
		score: 3.3,
	},
	{
		id: "q10",
		text: "Any additional comments or observations?",
		aiSummary:
			"Staff expressed appreciation for recent team lunches and highlighted excellent collaboration among the nursing staff despite challenging conditions.",
		isActive: false,
		score: 4.2,
	},
];

const getRandomDate = (daysAgo: number) => {
	const date = new Date();
	date.setDate(date.getDate() - Math.floor(Math.random() * daysAgo));
	date.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60));
	return date.toISOString();
};

export const SURVEY_RESPONSES: SurveyResponse[] = [
	{
		id: "r1-1",
		questionId: "q1",
		staffName: "Sarah Jenkins",
		role: "RN",
		responseText: "5. Everything went exactly as planned. Charting was done on time and the floor was quiet.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-2",
		questionId: "q1",
		staffName: "Marcus Thorne",
		role: "CNA",
		responseText: "3. It was okay, but I'm exhausted. We got the work done but it was non-stop moving.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-3",
		questionId: "q1",
		staffName: "Elena Rodriguez",
		role: "LPN",
		responseText: "4. Good shift overall. A few minor supply issues, but the teamwork was excellent.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-4",
		questionId: "q1",
		staffName: "David Wu",
		role: "RN",
		responseText: "5. One of the best shifts I've had in months. Highly productive and zero emergencies.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r1-5",
		questionId: "q1",
		staffName: "Aaliyah Smith",
		role: "CNA",
		responseText: "3. Average. We were fully staffed but the resident acuity was very high today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-6",
		questionId: "q1",
		staffName: "James Miller",
		role: "LPN",
		responseText: "4. Solid 4. Med pass was smooth, though the new admission took up some extra time.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-7",
		questionId: "q1",
		staffName: "Maria Garcia",
		role: "CNA",
		responseText: "4. I’m tired, but I felt like I actually helped my residents today. Good energy on the floor.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-8",
		questionId: "q1",
		staffName: "Karen Whitlock",
		role: "RN",
		responseText: "5. Very smooth. Managed to get ahead on my monthly summaries for once.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-9",
		questionId: "q1",
		staffName: "Brian O'Conner",
		role: "LPN",
		responseText: "4. No major complaints. Just the usual busy pace of a rehab wing.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-10",
		questionId: "q1",
		staffName: "Linda Zhao",
		role: "CNA",
		responseText: "3. It was fine, just very routine. Nothing special, but nothing went wrong.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-11",
		questionId: "q1",
		staffName: "Robert Taylor",
		role: "RN",
		responseText: "5. Excellent shift. Staffing was perfect and the residents were all stable.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-12",
		questionId: "q1",
		staffName: "Tasha Williams",
		role: "CNA",
		responseText: "3. 3 out of 5. I didn't get my full lunch break because of a shower schedule backup.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r1-13",
		questionId: "q1",
		staffName: "Sam Peterson",
		role: "LPN",
		responseText: "4. Decent shift. Good communication between the nurses and the aides today.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r1-14",
		questionId: "q1",
		staffName: "Emily Davis",
		role: "CNA",
		responseText: "3. Just okay. It felt a bit disorganized in the dining room this morning.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-15",
		questionId: "q1",
		staffName: "Michael Chang",
		role: "RN",
		responseText: "5. High satisfaction today. Felt like a true professional team on the unit.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-16",
		questionId: "q1",
		staffName: "Gloria Estefan",
		role: "LPN",
		responseText: "4. Above average. Most of the residents were in good spirits which helps a lot.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-17",
		questionId: "q1",
		staffName: "Jordan Reed",
		role: "CNA",
		responseText: "3. Middle of the road. We need better equipment, but the people I work with are great.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r1-18",
		questionId: "q1",
		staffName: "Rachel Green",
		role: "RN",
		responseText: "4. Good. A little hectic right at shift change, but the rest of the day was fine.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r1-19",
		questionId: "q1",
		staffName: "Oscar Isaacs",
		role: "LPN",
		responseText: "2. Honestly, a bit stressful. Had a lot of family questions that slowed me down.",
		timestamp: getRandomDate(30),
	},
	{
		id: "r2-1",
		questionId: "q2",
		staffName: "Aaliyah Smith",
		role: "CNA",
		responseText: "No. I had 16 residents on the heavy care wing. I couldn't even give everyone a proper wash.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-2",
		questionId: "q2",
		staffName: "Sonia Gupta",
		role: "RN",
		responseText: "Yes, the nursing side was fine. I had time to complete all my assessments and charting without staying late.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-3",
		questionId: "q2",
		staffName: "Marcus Thorne",
		role: "CNA",
		responseText: "Absolutely not. One call-out meant we were running with 3 aides for the whole floor. It's exhausting.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-4",
		questionId: "q2",
		staffName: "James Miller",
		role: "LPN",
		responseText: "Staffing was adequate for the med pass. I didn't feel rushed today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-5",
		questionId: "q2",
		staffName: "Tasha Williams",
		role: "CNA",
		responseText: "We are drowning. Management sees the RNs are okay and thinks the floor is fine, but the aides are breaking.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-6",
		questionId: "q2",
		staffName: "David Wu",
		role: "RN",
		responseText: "I felt supported. I had an extra LPN to help with treatments, which made the shift very smooth.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-7",
		questionId: "q2",
		staffName: "Maria Garcia",
		role: "CNA",
		responseText: "No. I spent the whole day answering lights for two different hallways. It's not safe for the residents.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-8",
		questionId: "q2",
		staffName: "Brian O'Conner",
		role: "LPN",
		responseText: "Yes, the shift was well-covered from a clinical standpoint. No complaints here.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-9",
		questionId: "q2",
		staffName: "Kevin Stamos",
		role: "CNA",
		responseText: "We needed at least two more CNAs. I couldn't get to my restorative nursing programs because I was too busy with basics.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-10",
		questionId: "q2",
		staffName: "Elena Rodriguez",
		role: "LPN",
		responseText: "Everything was fine on my end. The staffing levels met the grid requirements today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-11",
		questionId: "q2",
		staffName: "Linda Zhao",
		role: "CNA",
		responseText: "Too many two-person transfers and not enough people to help. I’m worried I’m going to hurt my back.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-12",
		questionId: "q2",
		staffName: "Sarah Jenkins",
		role: "RN",
		responseText: "Staffing was sufficient today. I was able to mentor a new hire since the floor was stable.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-13",
		questionId: "q2",
		staffName: "Derek Moore",
		role: "CNA",
		responseText: "No. It feels like the aides are always the ones who have to 'make do' while the office says we are fully staffed.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-14",
		questionId: "q2",
		staffName: "Michael Chang",
		role: "RN",
		responseText: "Yes, I felt we had the right mix of licensed staff to handle the current census.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-15",
		questionId: "q2",
		staffName: "Emily Davis",
		role: "CNA",
		responseText: "Short-staffed again. When we don't have enough aides, the residents are the ones who suffer.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-16",
		questionId: "q2",
		staffName: "Gloria Estefan",
		role: "LPN",
		responseText: "The nurse-to-patient ratio was good today. I felt like I could actually do my job properly.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-17",
		questionId: "q2",
		staffName: "Jordan Reed",
		role: "CNA",
		responseText: "No. Being short an aide makes the whole day a sprint. No time for breaks.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-18",
		questionId: "q2",
		staffName: "Rachel Green",
		role: "RN",
		responseText: "From my perspective, staffing was adequate. We had enough coverage for the med pass and treatments.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r2-19",
		questionId: "q2",
		staffName: "Oscar Isaacs",
		role: "LPN",
		responseText: "I felt okay. The workload was distributed fairly among the nursing staff.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r2-20",
		questionId: "q2",
		staffName: "Patty Hearn",
		role: "CNA",
		responseText: "No way. The aide-to-resident ratio was way too high today. We were running all shift.",
		timestamp: getRandomDate(30),
	},
	{
		id: "r3-1",
		questionId: "q3",
		staffName: "Alicia Varga",
		role: "CNA",
		responseText: "No. We ran out of wipes by 2:00 PM and had to use washcloths for everything.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-2",
		questionId: "q3",
		staffName: "Derek Moore",
		role: "LPN",
		responseText: "Mostly, but the med cart is sticking again. It makes the pass take 15 minutes longer than it should.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-3",
		questionId: "q3",
		staffName: "Sonia Gupta",
		role: "RN",
		responseText: "The support from the DON was actually great today. She helped me with a difficult family meeting.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-4",
		questionId: "q3",
		staffName: "Kevin Stamos",
		role: "CNA",
		responseText: "No, one of the Hoyer lifts is broken and the other one was on the other side of the building all day.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-5",
		questionId: "q3",
		staffName: "Jasmine Lee",
		role: "LPN",
		responseText: "Yes, the supply closet was actually stocked for once. No complaints today.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-6",
		questionId: "q3",
		staffName: "Terrence Hall",
		role: "RN",
		responseText: "No. I spent an hour hunting for a working pulse ox. We need more equipment that actually works.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-7",
		questionId: "q3",
		staffName: "Maria Hernandez",
		role: "CNA",
		responseText: "We are low on linens again. I had to go to three different floors just to find clean flat sheets.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-8",
		questionId: "q3",
		staffName: "Samuel Bekele",
		role: "LPN",
		responseText: "Support was lacking. I asked for help with a dressing change and everyone was too busy to assist.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-9",
		questionId: "q3",
		staffName: "Grace O'Malley",
		role: "RN",
		responseText: "Yes, I had all the clinical supplies I needed, and the pharmacy delivery was on time.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-10",
		questionId: "q3",
		staffName: "Lamar Jackson",
		role: "CNA",
		responseText: "No, we are out of gloves in size large. Having to use mediums that rip constantly is frustrating.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-11",
		questionId: "q3",
		staffName: "Brittany Spears",
		role: "LPN",
		responseText: "The wound care supplies were depleted. I had to improvise, which isn't ideal for the residents.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-12",
		questionId: "q3",
		staffName: "Frank Castle",
		role: "RN",
		responseText: "Resources were fine, but the IT system kept lagging, making charting a nightmare.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-13",
		questionId: "q3",
		staffName: "Anita Hill",
		role: "CNA",
		responseText: "Yes, we had enough briefs and barrier cream today, so care went a lot faster.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-14",
		questionId: "q3",
		staffName: "Steve Rogers",
		role: "LPN",
		responseText: "No. I needed the bladder scanner and it was missing its battery charger again.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-15",
		questionId: "q3",
		staffName: "Nancy Wheeler",
		role: "RN",
		responseText: "I felt supported by my team, but the lack of working thermometers is getting ridiculous.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-16",
		questionId: "q3",
		staffName: "Winston Zeddemore",
		role: "CNA",
		responseText: "No support. I was on the floor by myself for an hour during shift change.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-17",
		questionId: "q3",
		staffName: "Clara Oswald",
		role: "LPN",
		responseText: "Yes, the new supply ordering system seems to be working better this week.",
		timestamp: getRandomDate(30),
	},
	{
		id: "r3-18",
		questionId: "q3",
		staffName: "Homer Simpson",
		role: "CNA",
		responseText: "Ran out of trash bags for the bins halfway through. Had to use grocery bags from the breakroom.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r3-19",
		questionId: "q3",
		staffName: "Ellen Ripley",
		role: "RN",
		responseText: "Support was zero. Management stayed in their offices while we struggled with a surge in admissions.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r3-20",
		questionId: "q3",
		staffName: "Arthur Dent",
		role: "LPN",
		responseText: "Supplies were okay, but we really need more working blood pressure cuffs.",
		timestamp: getRandomDate(30),
	},
	{
		id: "r4-1",
		questionId: "q4",
		staffName: "Marcus Thorne",
		role: "CNA",
		responseText: "No, it was a surprisingly smooth day. No call-outs and everyone was in a good mood.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-2",
		questionId: "q4",
		staffName: "Elena Rodriguez",
		role: "LPN",
		responseText: "Not really. The pharmacy delivery was even a little early, so I wasn't rushing my med pass.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-3",
		questionId: "q4",
		staffName: "Sarah Jenkins",
		role: "RN",
		responseText: "Just a minor tech glitch with the eMAR, but it was fixed in five minutes. Otherwise, a very easy shift.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-4",
		questionId: "q4",
		staffName: "Aaliyah Smith",
		role: "CNA",
		responseText: "Everything went fine. We actually had enough help to get everyone dressed before lunch.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-5",
		questionId: "q4",
		staffName: "James Miller",
		role: "LPN",
		responseText: "Nothing out of the ordinary. It was one of those rare shifts where everything just clicked.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-6",
		questionId: "q4",
		staffName: "Maria Garcia",
		role: "CNA",
		responseText: "No, the day shift actually finished their vitals, so I wasn't playing catch-up.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-7",
		questionId: "q4",
		staffName: "David Wu",
		role: "RN",
		responseText: "Nothing made it harder. In fact, having a student nurse today actually made my documentation faster.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-8",
		questionId: "q4",
		staffName: "Brian O'Conner",
		role: "LPN",
		responseText: "Nope. No falls, no emergencies, and no broken equipment today.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-9",
		questionId: "q4",
		staffName: "Linda Zhao",
		role: "CNA",
		responseText: "It was a good shift. The residents were calm and we had plenty of linens.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-10",
		questionId: "q4",
		staffName: "Robert Taylor",
		role: "RN",
		responseText: "I had no unexpected admissions today, which allowed me to actually catch up on my care plans.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-11",
		questionId: "q4",
		staffName: "Tasha Williams",
		role: "CNA",
		responseText: "No issues. We worked well as a team and took our breaks on time.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-12",
		questionId: "q4",
		staffName: "Sam Peterson",
		role: "LPN",
		responseText: "A family member was a bit demanding, but we handled it quickly. Didn't slow me down much.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-13",
		questionId: "q4",
		staffName: "Emily Davis",
		role: "CNA",
		responseText: "No, it was very quiet. I even had time to sit and talk with a few residents.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-14",
		questionId: "q4",
		staffName: "Michael Chang",
		role: "RN",
		responseText: "The shift was very efficient. Management was on the floor helping out, which kept things moving.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-15",
		questionId: "q4",
		staffName: "Gloria Estefan",
		role: "LPN",
		responseText: "Nothing made it harder than it should be. The transition between shifts was seamless.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-17",
		questionId: "q4",
		staffName: "Jordan Reed",
		role: "CNA",
		responseText: "No, everything was in its place and the floor was well-staffed.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-18",
		questionId: "q4",
		staffName: "Rachel Green",
		role: "RN",
		responseText: "It was a standard, productive day. No major interruptions or 'surprises' from the lab.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-19",
		questionId: "q4",
		staffName: "Oscar Isaacs",
		role: "LPN",
		responseText: "The new scheduling software actually worked today, so that made life a lot easier.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r4-20",
		questionId: "q4",
		staffName: "Patty Hearn",
		role: "CNA",
		responseText: "No, today was great. I wish every shift was this organized.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r4-21",
		questionId: "q4",
		staffName: "Kevin Stamos",
		role: "CNA",
		responseText: "Just a small spill in the dining room, but we cleaned it up fast. No big deal.",
		timestamp: getRandomDate(90),
	},
	{
		id: "r5-1",
		questionId: "q5",
		staffName: "Aaliyah Smith",
		role: "CNA",
		responseText: "Not today. Whenever I needed a second person for a transfer, someone was available immediately.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r5-2",
		questionId: "q5",
		staffName: "Sonia Gupta",
		role: "RN",
		responseText: "No, we had a high-acuity admission, but since we were fully staffed, I could focus on them without neglecting the floor.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-3",
		questionId: "q5",
		staffName: "Marcus Thorne",
		role: "CNA",
		responseText: "No situations like that today. We had enough hands on deck to get all the morning care done safely.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-4",
		questionId: "q5",
		staffName: "James Miller",
		role: "LPN",
		responseText: "I felt well-supported even during the busy med pass. No safety concerns at all.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-5",
		questionId: "q5",
		staffName: "Tasha Williams",
		role: "CNA",
		responseText: "Usually it's hard, but today was great. I didn't have to wait for help with my total care residents.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-6",
		questionId: "q5",
		staffName: "David Wu",
		role: "RN",
		responseText: "No. We had a resident fall (minor), and we had plenty of staff to assist with the assessment and move.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-7",
		questionId: "q5",
		staffName: "Maria Garcia",
		role: "CNA",
		responseText: "Not at all. The team worked really well together today to make sure everyone was covered.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-8",
		questionId: "q5",
		staffName: "Brian O'Conner",
		role: "LPN",
		responseText: "No, the clinical staffing was perfect for the needs of my unit today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-9",
		questionId: "q5",
		staffName: "Kevin Stamos",
		role: "CNA",
		responseText: "I felt safe all shift. Every time I used the Hoyer, I had a partner ready to assist.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-10",
		questionId: "q5",
		staffName: "Elena Rodriguez",
		role: "LPN",
		responseText: "No situations felt understaffed. It was a very controlled environment today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-11",
		questionId: "q5",
		staffName: "Linda Zhao",
		role: "CNA",
		responseText: "Everything went smoothly. I never felt like I was struggling to keep up with resident needs.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-12",
		questionId: "q5",
		staffName: "Sarah Jenkins",
		role: "RN",
		responseText: "No. Even with a couple of residents acting out, we had enough staff to redirect them properly.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-13",
		questionId: "q5",
		staffName: "Derek Moore",
		role: "CNA",
		responseText: "For once, I didn't feel like I was in five places at once. We had enough people.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-14",
		questionId: "q5",
		staffName: "Michael Chang",
		role: "RN",
		responseText: "No, the staffing levels were more than adequate for the level of care required today.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-15",
		questionId: "q5",
		staffName: "Emily Davis",
		role: "CNA",
		responseText: "No, I actually felt like I could take my time with each resident instead of rushing.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r5-16",
		questionId: "q5",
		staffName: "Gloria Estefan",
		role: "LPN",
		responseText: "No, everything was handled professionally and with plenty of backup.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r5-17",
		questionId: "q5",
		staffName: "Jordan Reed",
		role: "CNA",
		responseText: "It was a good day. No one was left waiting for care because we were short.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r5-18",
		questionId: "q5",
		staffName: "Rachel Green",
		role: "RN",
		responseText: "I didn't encounter a single moment where I felt we lacked the necessary personnel.",
		timestamp: getRandomDate(90),
	},

	{
		id: "r5-19",
		questionId: "q5",
		staffName: "Oscar Isaacs",
		role: "LPN",
		responseText: "Adequate staffing meant we could handle the daily routine and the extras without stress.",
		timestamp: getRandomDate(30),
	},

	{
		id: "r5-20",
		questionId: "q5",
		staffName: "Patty Hearn",
		role: "CNA",
		responseText: "No issues. We had a great team today and everyone pulled their weight.",
		timestamp: getRandomDate(30),
	},
];

export const DEFAULT_SETTINGS: SurveySettings = {
	sendToAllStaff: true,
	sendAllQuestions: true,
	randomQuestionCount: 3,
	activeQuestionIds: SURVEY_QUESTIONS.filter((q) => q.isActive).map((q) => q.id),
};
